import React, { useState, useEffect, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import styled from 'styled-components';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Class, ClassMember, User } from '../types/alarm';
import { userDataService } from '../services/userDataService';
import ConfirmModal from './ConfirmModal';
import {
  checkStudentLimit,
  checkMemberGroupAccess,
  getLicenseLimits,
  isStandardPlanOrAbove
} from '../utils/licenseLimits';
import LicenseLimitModal from './LicenseLimitModal';
import WarningModal from './WarningModal';
import { getOrderedMemberGroups, sanitizeMemberGroupOrder } from '../utils/memberGroupOrder';

interface ClassSettingsModalProps {
  isOpen: boolean;
  classData: Class;
  userId: string;  // 사용자 ID 추가
  user: User; // 사용자 정보 추가 (라이센스 체크용)
  /** 메인 페이지에서 현재 선택된 멤버 그룹 (예: 'A', 'B') */
  currentMemberGroup?: string;
  onClose: () => void;
  onSave: (updatedClass: Class) => void;
  getExistingClassCodes: () => Promise<string[]>;
  onMembersChange?: () => void; // 멤버 정보 변경 시 호출될 콜백
  onClassReset?: () => void; // 클래스 초기화 시 호출될 콜백 (캐시 초기화용)
  onLicenseClick?: () => void; // 이용권 구매 모달 열기
}

interface DeletedMemberSummary {
  member: ClassMember;
  groupKey: string;
  groupName: string;
}

type PendingGroupAction =
  | { type: 'select'; groupKey: string }
  | { type: 'add' };

// 클래스 설정 모달 헤더용 스타일 (ClassSettingsModal 위에 정의 필요)
const ModalTitle = styled.h2`
  margin: 0 0 24px 0;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-text-primary);
  text-align: center;
`;

const ModalHeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 24px;
  min-height: 2rem;

  & ${ModalTitle} {
    margin: 0;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    width: max-content;
  }
`;

const ResetClassButtonHeader = styled.button`
  flex-shrink: 0;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 드래그 가능한 멤버 행 컴포넌트
const SortableMemberRow: React.FC<{
  member: ClassMember;
  updateMember: (id: string, field: keyof ClassMember, value: string) => void;
  removeMember: (id: string) => void;
  isManagerEditMode?: boolean;
  onManagerChange?: (id: string, checked: boolean) => void;
}> = ({ member, updateMember, removeMember, isManagerEditMode, onManagerChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: member.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <MemberRow ref={setNodeRef} style={style} $managerColumn={!!isManagerEditMode}>
      {isManagerEditMode && (
        <MemberCell>
          <ManagerCheckbox
            type="checkbox"
            checked={!!member.isManager}
            onChange={(e) => onManagerChange?.(member.id, e.target.checked)}
            aria-label={`${member.name} 매니저 권한`}
          />
        </MemberCell>
      )}
      <MemberCell>
        <MemberInput
          type="text"
          value={member.number}
          onChange={(e) => updateMember(member.id, 'number', e.target.value)}
          placeholder="번호"
          style={{ borderRight: 'none' }}
        />
      </MemberCell>
      <MemberCell>
        <NameCellWithBadge>
          <ManagerBadgeSlot>
            {member.isManager && <ManagerBadge>M</ManagerBadge>}
          </ManagerBadgeSlot>
          <NameInputWrap>
            <MemberInput
              type="text"
              value={member.name}
              onChange={(e) => updateMember(member.id, 'name', e.target.value)}
              placeholder="이름"
              style={{ borderRight: 'none' }}
            />
          </NameInputWrap>
        </NameCellWithBadge>
      </MemberCell>
      <MemberCell>
        <MemberInput
          type="text"
          value={member.accessCode}
          onChange={(e) => updateMember(member.id, 'accessCode', e.target.value)}
          placeholder="입장코드"
          style={{ borderRight: 'none' }}
        />
      </MemberCell>
      <MemberCell>
        <DeleteMemberButton onClick={() => removeMember(member.id)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
          </svg>
        </DeleteMemberButton>
      </MemberCell>
      <MemberCell>
        <DragHandle {...attributes} {...listeners} aria-label="순서 조정">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M6 7h12v2H6V7zm0 4h12v2H6v-2zm0 4h12v2H6v-2z"/>
          </svg>
        </DragHandle>
      </MemberCell>
    </MemberRow>
  );
};

const SortableGroupTab: React.FC<{
  groupKey: string;
  isActive: boolean;
  label: string;
  onClick: () => void;
  onDoubleClick: (e: React.MouseEvent) => void;
}> = ({ groupKey, isActive, label, onClick, onDoubleClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: groupKey });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <MemberGroupTab
        {...attributes}
        {...listeners}
        $isActive={isActive}
        type="button"
        title="드래그하여 그룹 순서 변경, 더블클릭하여 그룹 이름 수정"
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      >
        {label}
      </MemberGroupTab>
    </div>
  );
};

const ClassSettingsModal: React.FC<ClassSettingsModalProps> = ({
  isOpen,
  classData,
  userId,
  user,
  currentMemberGroup,
  onClose,
  onSave,
  getExistingClassCodes,
  onMembersChange,
  onClassReset,
  onLicenseClick
}) => {
  const [classCode, setClassCode] = useState<string>('');
  const [classEntryCode, setClassEntryCode] = useState<string>('');
  const [members, setMembers] = useState<ClassMember[]>([]);
  const [memberGroups, setMemberGroups] = useState<string[]>(['A']);
  const [memberGroupNames, setMemberGroupNames] = useState<{ [groupKey: string]: string }>({});
  const [memberGroupOrder, setMemberGroupOrder] = useState<string[]>(['A']);
  const [selectedMemberGroup, setSelectedMemberGroup] = useState<string>(
    (currentMemberGroup || 'A').toString().trim().toUpperCase()
  );
  const [editingGroupKey, setEditingGroupKey] = useState<string | null>(null);
  const [editingGroupName, setEditingGroupName] = useState<string>('');
  const [isEditingCode, setIsEditingCode] = useState(false);
  const [error, setError] = useState<string>('');
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [deletedMembers, setDeletedMembers] = useState<DeletedMemberSummary[]>([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetError, setResetError] = useState<string>('');
  const [isResetting, setIsResetting] = useState(false);
  const [initialClassCode, setInitialClassCode] = useState<string>('');
  const [initialClassEntryCode, setInitialClassEntryCode] = useState<string>('');
  const [initialMembersByGroup, setInitialMembersByGroup] = useState<Record<string, ClassMember[]>>({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [initialMemberGroups, setInitialMemberGroups] = useState<string[]>(['A']);
  const [initialMemberGroupOrder, setInitialMemberGroupOrder] = useState<string[]>(['A']);
  const [isManagerEditMode, setIsManagerEditMode] = useState(false);
  const [initialMemberGroupNames, setInitialMemberGroupNames] = useState<{ [groupKey: string]: string }>({});
  const [showGroupDeleteWarning, setShowGroupDeleteWarning] = useState(false);
  const [deletedGroups, setDeletedGroups] = useState<string[]>([]);
  const [showRemoveGroupConfirmModal, setShowRemoveGroupConfirmModal] = useState(false);
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  const [membersByGroup, setMembersByGroup] = useState<Record<string, ClassMember[]>>({});
  const [isInitializing, setIsInitializing] = useState(false);
  const [showGroupSwitchConfirm, setShowGroupSwitchConfirm] = useState(false);
  const [pendingGroupAction, setPendingGroupAction] = useState<PendingGroupAction | null>(null);
  const [isDeleteProcessing, setIsDeleteProcessing] = useState(false);
  const wasOpenRef = useRef(false);
  const prevSelectedGroupRef = useRef<string>(selectedMemberGroup);
  const afterSaveActionRef = useRef<(() => void) | null>(null);
  const suppressSaveSuccessModalRef = useRef(false);

  const cloneMembers = (list: ClassMember[]) => JSON.parse(JSON.stringify(list)) as ClassMember[];

  const areMemberListsEqual = (left: ClassMember[], right: ClassMember[]) => {
    if (left.length !== right.length) return false;

    for (let i = 0; i < left.length; i += 1) {
      const leftMember = left[i];
      const rightMember = right[i];

      if (
        !rightMember ||
        leftMember.id !== rightMember.id ||
        leftMember.name !== rightMember.name ||
        leftMember.number !== rightMember.number ||
        leftMember.accessCode !== rightMember.accessCode ||
        !!leftMember.isManager !== !!rightMember.isManager
      ) {
        return false;
      }
    }

    return true;
  };

  const areStringArraysEqual = (left: string[], right: string[]) => {
    if (left.length !== right.length) return false;
    return left.every((value, index) => value === right[index]);
  };

  const areGroupNameMapsEqual = (
    left: { [groupKey: string]: string },
    right: { [groupKey: string]: string }
  ) => {
    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(right).sort();
    if (!areStringArraysEqual(leftKeys, rightKeys)) return false;
    return leftKeys.every(key => left[key] === right[key]);
  };

  const getMergedMembersByGroup = () => ({
    ...membersByGroup,
    [selectedMemberGroup]: members
  });

  const orderedMemberGroups = useMemo(
    () => getOrderedMemberGroups(memberGroups, memberGroupOrder),
    [memberGroups, memberGroupOrder]
  );

  const clearDeferredSaveCompletion = () => {
    afterSaveActionRef.current = null;
    suppressSaveSuccessModalRef.current = false;
  };

  const finishSaveFlow = () => {
    const afterSaveAction = afterSaveActionRef.current;
    const suppressSuccessModal = suppressSaveSuccessModalRef.current;
    clearDeferredSaveCompletion();

    if (afterSaveAction) {
      afterSaveAction();
    }

    if (!suppressSuccessModal) {
      setShowSaveSuccessModal(true);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    if (justOpened) {
      const initializeData = async () => {
        try {
          setIsInitializing(true);
          // 클래스 코드 초기화 - 코드가 없으면 빈 문자열로 설정 (물음표로 표시됨)
          let initialCode = classData.classCode || '';
          // 자동 생성하지 않음 - 사용자가 직접 입력하거나 생성 버튼을 눌러야 함
          setClassCode(initialCode);
          setInitialClassCode(initialCode);
          const initialEntryCode = classData.classEntryCode || '';
          setClassEntryCode(initialEntryCode);
          setInitialClassEntryCode(initialEntryCode);

          // 멤버 그룹 초기화 (없으면 A만)
          let groups = (classData.memberGroups && classData.memberGroups.length > 0)
            ? classData.memberGroups
            : ['A'];
          const normalizedGroups = getOrderedMemberGroups(groups);
          
          // 멤버 그룹 이름 초기화
          const initialNames = classData.memberGroupNames || {};
          setMemberGroupNames(initialNames);
          setInitialMemberGroupNames({ ...initialNames }); // 초기 그룹 이름 저장
          
          // 프리·베이직은 첫 번째 그룹만, 스탠다드 이상은 설정된 그룹 전체 사용
          const canUseMultipleGroups = isStandardPlanOrAbove(user.userType);
          const groupsToUse = canUseMultipleGroups ? normalizedGroups : [normalizedGroups[0] || 'A'];
          const orderToUse = sanitizeMemberGroupOrder(
            groupsToUse,
            canUseMultipleGroups ? classData.memberGroupOrder : [groupsToUse[0] || 'A']
          );
          setMemberGroups(groupsToUse);
          setInitialMemberGroups([...groupsToUse]); // 초기 그룹 목록 저장
          setMemberGroupOrder(orderToUse);
          setInitialMemberGroupOrder([...orderToUse]);

          // 모달이 열릴 때는 메인에서 선택한 그룹(currentMemberGroup)을 우선 사용
          // - currentMemberGroup이 없거나 사용 불가하면 첫 번째 그룹으로 폴백
          const normalizedCurrent =
            (currentMemberGroup || '').toString().trim().toUpperCase();
          const preferredGroup = orderToUse.includes(normalizedCurrent)
            ? normalizedCurrent
            : (orderToUse[0] || 'A');

          const key = `selected_member_group_${userId}_${classData.id}`;
          localStorage.setItem(key, preferredGroup);
          const safeGroup = preferredGroup;
          setSelectedMemberGroup(safeGroup);

          // 기존 그룹 캐시는 초기화 (클래스/유저 변경 시 뒤섞임 방지)
          setMembersByGroup({});
          setMembers([]);
          setInitialMembersByGroup({});
          // 멤버 데이터를 컬렉션에서 로드 (선택 그룹 기준)
          console.log('🔍 멤버 데이터 로드 시작:', { userId, classId: classData.id, memberGroup: safeGroup });
          const loadedMembers = await userDataService.loadClassMembers(userId, classData.id, safeGroup);
          setMembersByGroup({ [safeGroup]: loadedMembers });
          setMembers(loadedMembers);
          setInitialMembersByGroup({ [safeGroup]: cloneMembers(loadedMembers) });

          console.log('✅ 멤버 데이터 로드 완료:', { membersCount: loadedMembers.length, memberGroup: safeGroup });
          setShowCancelConfirm(false);
        } catch (error) {
          console.error('멤버 데이터 로드 실패:', error);
          // 백업: 기존 배열 구조에서 로드 시도
          setMembers((classData as any).members || []);
        } finally {
          setIsInitializing(false);
        }
      };
      
      initializeData();
      setError('');
      setIsEditingCode(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, classData, userId, user.userType, currentMemberGroup]);

  // 그룹 선택 변경 시에만: 로컬에 있으면 복원, 없으면 서버에서 로드
  // groupChanged 체크로, membersByGroup 변경(입력 동기화) 시 불필요한 setMembers 호출 방지 → 타이핑 깜빡임 해결
  useEffect(() => {
    if (!isOpen) return;
    if (isInitializing) return;
    if (!selectedMemberGroup) return;

    const groupChanged = prevSelectedGroupRef.current !== selectedMemberGroup;
    prevSelectedGroupRef.current = selectedMemberGroup;

    if (!groupChanged) return;

    const cached = membersByGroup[selectedMemberGroup];
    if (cached !== undefined) {
      setMembers(cached);
      return;
    }

    const loadForGroup = async () => {
      try {
        const key = `selected_member_group_${userId}_${classData.id}`;
        localStorage.setItem(key, selectedMemberGroup);
        console.log('🔄 멤버 그룹 변경 - 멤버 로드:', { memberGroup: selectedMemberGroup });
        const loadedMembers = await userDataService.loadClassMembers(userId, classData.id, selectedMemberGroup);
        setMembersByGroup(prev => ({ ...prev, [selectedMemberGroup]: loadedMembers }));
        setInitialMembersByGroup(prev => ({ ...prev, [selectedMemberGroup]: cloneMembers(loadedMembers) }));
        setMembers(loadedMembers);
      } catch (error) {
        console.error('멤버 그룹 변경 시 로드 실패:', error);
      }
    };

    void loadForGroup();
  }, [selectedMemberGroup, isOpen, isInitializing, membersByGroup, userId, classData.id]);

  // 현재 선택 그룹의 멤버 변경사항을 로컬 캐시에 동기화
  useEffect(() => {
    if (!isOpen) return;
    if (!selectedMemberGroup) return;
    setMembersByGroup(prev => {
      if (prev[selectedMemberGroup] === members) return prev;
      return { ...prev, [selectedMemberGroup]: members };
    });
  }, [isOpen, selectedMemberGroup, members]);

  // 그룹 이름 표시 헬퍼 함수
  const getGroupDisplayName = (groupKey: string): string => {
    return memberGroupNames[groupKey] || `${groupKey}그룹`;
  };

  // 그룹 이름 편집 시작
  const handleStartEditGroupName = (groupKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingGroupKey(groupKey);
    setEditingGroupName(memberGroupNames[groupKey] || '');
  };

  // 그룹 이름 편집 완료
  const handleFinishEditGroupName = () => {
    if (editingGroupKey) {
      const trimmedName = editingGroupName.trim();
      if (trimmedName.length > 5) {
        setWarningMessage('그룹 이름은 최대 5글자까지 입력 가능합니다.');
        setShowWarningModal(true);
        return;
      }
      
      const updatedNames = { ...memberGroupNames };
      if (trimmedName) {
        updatedNames[editingGroupKey] = trimmedName;
      } else {
        // 빈 문자열이면 기본값으로 돌아가기 (필드 삭제)
        delete updatedNames[editingGroupKey];
      }
      setMemberGroupNames(updatedNames);
    }
    setEditingGroupKey(null);
    setEditingGroupName('');
  };

  // 그룹 이름 편집 취소
  const handleCancelEditGroupName = () => {
    setEditingGroupKey(null);
    setEditingGroupName('');
  };

  // [-그룹] 클릭: 1차 안내 후 그룹 삭제 확인 모달에서 서버에 즉시 반영
  const handleRemoveGroupClick = () => {
    if (memberGroups.length <= 1) {
      setWarningMessage('그룹은 최소 1개 이상이어야 합니다.');
      setShowWarningModal(true);
      return;
    }
    setShowRemoveGroupConfirmModal(true);
  };

  const hasGroupEditingChanges = useMemo(() => {
    if (!areStringArraysEqual(memberGroups, initialMemberGroups)) return true;
    if (!areStringArraysEqual(memberGroupOrder, initialMemberGroupOrder)) return true;
    if (!areGroupNameMapsEqual(memberGroupNames, initialMemberGroupNames)) return true;

    const mergedMembersByGroup = getMergedMembersByGroup();
    const groupKeys = Array.from(
      new Set([
        ...Object.keys(mergedMembersByGroup),
        ...Object.keys(initialMembersByGroup),
        ...memberGroups,
        ...initialMemberGroups
      ])
    );

    return groupKeys.some(groupKey => {
      const currentGroupMembers = mergedMembersByGroup[groupKey] || [];
      const initialGroupMembers = initialMembersByGroup[groupKey] || [];
      return !areMemberListsEqual(currentGroupMembers, initialGroupMembers);
    });
  }, [
    memberGroups,
    initialMemberGroups,
    memberGroupOrder,
    initialMemberGroupOrder,
    memberGroupNames,
    initialMemberGroupNames,
    initialMembersByGroup,
    areGroupNameMapsEqual,
    getMergedMembersByGroup
  ]);

  /** 그룹 순서만 바뀌고 그룹 목록·이름·멤버는 초기와 동일한 경우 */
  const hasOnlyMemberGroupOrderChanges = useMemo(() => {
    if (areStringArraysEqual(memberGroupOrder, initialMemberGroupOrder)) return false;
    if (!areStringArraysEqual(memberGroups, initialMemberGroups)) return false;
    if (!areGroupNameMapsEqual(memberGroupNames, initialMemberGroupNames)) return false;

    const mergedMembersByGroup = getMergedMembersByGroup();
    const groupKeys = Array.from(
      new Set([
        ...Object.keys(mergedMembersByGroup),
        ...Object.keys(initialMembersByGroup),
        ...memberGroups,
        ...initialMemberGroups
      ])
    );

    return groupKeys.every(groupKey => {
      const currentGroupMembers = mergedMembersByGroup[groupKey] || [];
      const initialGroupMembers = initialMembersByGroup[groupKey] || [];
      return areMemberListsEqual(currentGroupMembers, initialGroupMembers);
    });
  }, [
    memberGroupOrder,
    initialMemberGroupOrder,
    memberGroups,
    initialMemberGroups,
    memberGroupNames,
    initialMemberGroupNames,
    initialMembersByGroup,
    membersByGroup,
    selectedMemberGroup,
    members,
    areGroupNameMapsEqual,
    getMergedMembersByGroup
  ]);

  const applyGroupAction = (
    action: PendingGroupAction,
    baseGroups: string[] = memberGroups
  ) => {
    if (action.type === 'select') {
      setSelectedMemberGroup(action.groupKey);
      return;
    }

    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const used = new Set(baseGroups);
    const next = alphabet.find(ch => !used.has(ch));
    if (!next) {
      setWarningMessage('더 이상 추가할 수 있는 그룹이 없습니다.');
      setShowWarningModal(true);
      return;
    }

    setMemberGroups([...baseGroups, next]);
    setMemberGroupOrder(prev => sanitizeMemberGroupOrder([...baseGroups, next], [...prev, next]));
    setMembersByGroup(prev => ({ ...prev, [next]: prev[next] ?? [] }));
    setSelectedMemberGroup(next);
  };

  const discardGroupEditingChanges = () => {
    const restoredGroups = [...initialMemberGroups];
    const restoredNames = { ...initialMemberGroupNames };
    const restoredOrder = [...initialMemberGroupOrder];
    const restoredMembersByGroup = Object.entries(initialMembersByGroup).reduce<Record<string, ClassMember[]>>(
      (acc, [groupKey, groupMembers]) => {
        acc[groupKey] = cloneMembers(groupMembers);
        return acc;
      },
      {}
    );

    setMemberGroups(restoredGroups);
    setMemberGroupNames(restoredNames);
    setMemberGroupOrder(restoredOrder);
    setMembersByGroup(restoredMembersByGroup);
    setMembers(cloneMembers(restoredMembersByGroup[selectedMemberGroup] || []));
    setEditingGroupKey(null);
    setEditingGroupName('');

    return { restoredGroups };
  };

  const requestGroupAction = (action: PendingGroupAction) => {
    if (editingGroupKey) {
      handleFinishEditGroupName();
    }

    if (hasGroupEditingChanges) {
      setPendingGroupAction(action);
      setShowGroupSwitchConfirm(true);
      return;
    }

    applyGroupAction(action);
  };

  const validateClassCode = async (code: string): Promise<boolean> => {
    // 클래스 코드가 없거나 물음표인 경우 검증하지 않음 (저장하지 않음)
    if (!code || code === '????????' || code.trim() === '') {
      setError('');
      return true; // 검증 통과 (저장하지 않음)
    }
    
    if (!/^\d{8}$/.test(code)) {
      setError('클래스 코드는 8자리 숫자여야 합니다.');
      return false;
    }
    
    try {
      // 현재 편집 중인 클래스의 원래 코드가 아닌 경우에만 중복 검사
      if (code !== classData.classCode) {
        const isUsed = await userDataService.isClassCodeUsed(code);
        if (isUsed) {
          setError('이미 다른 계정 또는 클래스에서 사용 중인 클래스 코드입니다.');
          return false;
        }
      }
      
      setError('');
      return true;
    } catch (error) {
      console.error('클래스 코드 검증 중 오류:', error);
      setError('클래스 코드 검증 중 오류가 발생했습니다.');
      return false;
    }
  };

  const handleClassCodeChange = (newCode: string) => {
    setClassCode(newCode);
    if (newCode.length === 8) {
      validateClassCode(newCode).catch(error => {
        console.error('클래스 코드 검증 실패:', error);
      });
    }
  };

  const handleClassCodeDoubleClick = () => {
    // 프리/베이직 플랜은 클래스 코드 편집 불가
    if (user.userType === 'free' || user.userType === 'basic') {
      return;
    }
    setIsEditingCode(true);
  };

  const handleClassCodeBlur = () => {
    setIsEditingCode(false);
    if (classCode) {
      validateClassCode(classCode).catch(error => {
        console.error('클래스 코드 검증 실패:', error);
      });
    }
  };

  const handleRandomCodeGenerate = async () => {
    // 프리/베이직 플랜은 클래스 코드 생성 불가
    if (user.userType === 'free' || user.userType === 'basic') {
      return;
    }
    try {
      const randomCode = await userDataService.generateUniqueClassCode();
      setClassCode(randomCode);
      setError(''); // 유니크한 코드이므로 에러 초기화
    } catch (error) {
      console.error('랜덤 클래스 코드 생성 실패:', error);
      setError('랜덤 클래스 코드 생성에 실패했습니다.');
    }
  };

  // 변경사항 감지
  const hasChanges = useMemo(() => {
    // 클래스 코드 변경 확인 (물음표는 변경사항으로 간주하지 않음)
    const currentCode = (classCode && classCode.trim() !== '' && classCode !== '????????') ? classCode : '';
    const initialCode = (initialClassCode && initialClassCode.trim() !== '' && initialClassCode !== '????????') ? initialClassCode : '';
    if (currentCode !== initialCode) return true;

    // 클래스 입장코드 변경 확인
    const currentEntryCode = classEntryCode.trim();
    const initialEntryCode = initialClassEntryCode.trim();
    if (currentEntryCode !== initialEntryCode) return true;

    return hasGroupEditingChanges;
  }, [classCode, classEntryCode, initialClassCode, initialClassEntryCode, hasGroupEditingChanges]);

  // 취소 핸들러
  const handleCancel = () => {
    console.log('🔍 handleCancel 호출됨, hasChanges:', hasChanges);
    if (hasChanges) {
      console.log('✅ 변경사항 있음 - 취소 확인 모달 표시');
      setShowCancelConfirm(true);
    } else {
      console.log('❌ 변경사항 없음 - 바로 닫기');
      onClose();
    }
  };

  const buildUpdatedClass = (
    groupsToUse: string[],
    membersMap: Record<string, ClassMember[]>,
    activeGroup: string = selectedMemberGroup,
    namesForClass?: { [groupKey: string]: string },
    orderForClass?: string[]
  ): Class => {
    const isFreeOrBasic = user.userType === 'free' || user.userType === 'basic';
    const hasValidClassCode = classCode && classCode !== '????????' && classCode.trim() !== '';
    const activeMembers = membersMap[activeGroup] ?? [];
    const names = namesForClass ?? memberGroupNames;
    const order = sanitizeMemberGroupOrder(groupsToUse, orderForClass ?? memberGroupOrder);

    return {
      ...classData,
      classCode: (!isFreeOrBasic && hasValidClassCode) ? classCode : (classData.classCode || ''),
      classEntryCode: classEntryCode.trim(),
      memberCount: activeMembers.length,
      memberGroups: groupsToUse,
      memberGroupNames: Object.keys(names).length > 0 ? names : undefined,
      memberGroupOrder: order
    };
  };

  const syncInitialStateAfterSave = (updatedClass: Class, savedMembersByGroup: Record<string, ClassMember[]>) => {
    const normalizedGroups = (updatedClass.memberGroups && updatedClass.memberGroups.length > 0)
      ? updatedClass.memberGroups
      : ['A'];
    const nextInitialMembersByGroup = { ...initialMembersByGroup };

    Object.keys(nextInitialMembersByGroup).forEach(groupKey => {
      if (!normalizedGroups.includes(groupKey)) {
        delete nextInitialMembersByGroup[groupKey];
      }
    });

    normalizedGroups.forEach(groupKey => {
      if (savedMembersByGroup[groupKey] !== undefined) {
        nextInitialMembersByGroup[groupKey] = cloneMembers(savedMembersByGroup[groupKey]);
      }
    });

    setInitialClassCode(updatedClass.classCode || '');
    setInitialClassEntryCode(updatedClass.classEntryCode || '');
    setInitialMemberGroups([...normalizedGroups]);
    setInitialMemberGroupNames({ ...(updatedClass.memberGroupNames || {}) });
    setInitialMemberGroupOrder(sanitizeMemberGroupOrder(normalizedGroups, updatedClass.memberGroupOrder));
    setMemberGroupNames(
      updatedClass.memberGroupNames && Object.keys(updatedClass.memberGroupNames).length > 0
        ? { ...updatedClass.memberGroupNames }
        : {}
    );
    setMemberGroupOrder(sanitizeMemberGroupOrder(normalizedGroups, updatedClass.memberGroupOrder));
    setInitialMembersByGroup(nextInitialMembersByGroup);
  };

  const getDeletedMembersSummary = async (
    groupsToSave: string[],
    membersMap: Record<string, ClassMember[]>,
    removedGroups: string[]
  ) => {
    const groupsToInspect = groupsToSave.filter(groupKey =>
      !removedGroups.includes(groupKey) && membersMap[groupKey] !== undefined
    );

    const results = await Promise.all(
      groupsToInspect.map(async (groupKey) => {
        const existingMembers = await userDataService.loadClassMembers(userId, classData.id, groupKey);
        const currentMemberIds = new Set((membersMap[groupKey] || []).map(member => member.id));

        return existingMembers
          .filter(member => !currentMemberIds.has(member.id))
          .map(member => ({
            member,
            groupKey,
            groupName: getGroupDisplayName(groupKey)
          }));
      })
    );

    return results.flat();
  };

  const persistClassAndMembers = async (
    groupsToSave: string[],
    membersMap: Record<string, ClassMember[]>,
    activeGroup: string = selectedMemberGroup,
    namesForClass?: { [groupKey: string]: string },
    orderForClass?: string[]
  ) => {
    await saveMembersData({
      memberGroups: groupsToSave,
      membersByGroup: membersMap,
      selectedMemberGroup: activeGroup,
      currentGroupMembers: membersMap[activeGroup] ?? members
    });

    const updatedClass = buildUpdatedClass(groupsToSave, membersMap, activeGroup, namesForClass, orderForClass);
    onSave(updatedClass);

    if (onMembersChange) {
      onMembersChange();
    }

    syncInitialStateAfterSave(updatedClass, membersMap);
    if (isManagerEditMode) {
      setIsManagerEditMode(false);
    }

    finishSaveFlow();
  };

  // 멤버 데이터 저장 함수 (모든 그룹, 과제 데이터 정리 포함)
  const saveMembersData = async (override?: {
    memberGroups: string[];
    membersByGroup: Record<string, ClassMember[]>;
    selectedMemberGroup: string;
    currentGroupMembers: ClassMember[];
  }) => {
    const groupsToSave = override?.memberGroups ?? memberGroups;
    const byGroup = override?.membersByGroup ?? membersByGroup;
    const currentGroup = override?.selectedMemberGroup ?? selectedMemberGroup;
    const currentList = override?.currentGroupMembers ?? members;
    try {
      for (const g of groupsToSave) {
        const list = g === currentGroup ? currentList : byGroup[g];
        if (list === undefined) continue;
        const existingMembers = await userDataService.loadClassMembers(userId, classData.id, g);
        const currentMemberIds = new Set(list.map(m => m.id));
        const deletedMembers = existingMembers.filter(m => !currentMemberIds.has(m.id));
        const deletedMemberIds = deletedMembers.map(m => m.id);
        if (deletedMemberIds.length > 0) {
          await userDataService.deleteStudentsWithAssignmentCleanup(userId, classData.id, deletedMemberIds, g);
        }
        await userDataService.saveClassMembersBatch(userId, classData.id, list, g);
      }
    } catch (error) {
      console.error('멤버 데이터 저장 실패:', error);
      throw error;
    }
  };

  const addMember = () => {
    const groupMemberLimit =
      user.userType === 'standard' || user.userType === 'pro' || user.userType === 'admin' ? 35 : 30;

    // 그룹당 최대 멤버 수 제한 (플랜별)
    if (members.length >= groupMemberLimit) {
      setWarningMessage(`멤버는 그룹당 최대 ${groupMemberLimit}명까지 추가할 수 있습니다.`);
      setShowWarningModal(true);
      return;
    }

    // 학생 수 제한 체크 (라이센스와 무관)
    const limitCheck = checkStudentLimit(user, members.length);
    if (!limitCheck.allowed) {
      setWarningMessage(limitCheck.message || '');
      setShowWarningModal(true);
      return;
    }
    
    // 현재 사용 중인 번호들을 추출
    const usedNumbers = members
      .map(member => parseInt(member.number))
      .filter(num => !isNaN(num) && num > 0);
    
    // 가장 큰 번호 찾기
    const maxNumber = usedNumbers.length > 0 ? Math.max(...usedNumbers) : 0;
    const nextNumber = maxNumber + 1;
    
    const newMember: ClassMember = {
      id: `member-${selectedMemberGroup}-${Date.now()}`,
      number: nextNumber.toString(),
      name: '',
      accessCode: '0000',  // 초기값은 0000으로 설정
      notes: '',
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    // 로컬 상태만 업데이트 (저장 버튼 눌렀을 때 서버 반영)
    setMembers([...members, newMember]);
  };

  const updateMember = (id: string, field: keyof ClassMember, value: string) => {
    const updatedMembers = members.map(member => {
      if (member.id === id) {
        const updatedMember = { 
          ...member, 
          [field]: value,
          updatedAt: new Date()
        };
        
        // 번호가 변경되면 입장코드는 그대로 유지 (사용자가 직접 수정 가능)
        return updatedMember;
      }
      return member;
    });
    
    // 로컬 상태만 업데이트 (저장 버튼 눌렀을 때 서버 반영)
    setMembers(updatedMembers);
  };

  const removeMember = (id: string) => {
    // 로컬 상태만 업데이트 (저장 버튼 눌렀을 때 서버 반영)
    setMembers(members.filter(member => member.id !== id));
  };

  const updateMemberManager = (id: string, checked: boolean) => {
    setMembers(members.map(m => m.id === id ? { ...m, isManager: checked, updatedAt: new Date() } : m));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setMembers((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over?.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setMemberGroupOrder(prev => {
      const currentOrder = sanitizeMemberGroupOrder(memberGroups, prev);
      const oldIndex = currentOrder.findIndex(groupKey => groupKey === active.id);
      const newIndex = currentOrder.findIndex(groupKey => groupKey === over.id);

      if (oldIndex < 0 || newIndex < 0) return currentOrder;
      return arrayMove(currentOrder, oldIndex, newIndex);
    });
  };

  const handleSave = async (options?: {
    afterSaveAction?: () => void;
    suppressSuccessModal?: boolean;
  }) => {
    if (isSaving) return; // 이미 저장 중이면 중복 실행 방지

    afterSaveActionRef.current = options?.afterSaveAction || null;
    suppressSaveSuccessModalRef.current = !!options?.suppressSuccessModal;

    // 즉시 UI 업데이트를 위해 flushSync 사용
    flushSync(() => {
      setIsSaving(true); // 저장 시작
    });

    // 프리/베이직 플랜이 아니고 클래스 코드가 있는 경우에만 검증
    const isFreeOrBasic = user.userType === 'free' || user.userType === 'basic';
    const hasValidClassCode = classCode && classCode !== '????????' && classCode.trim() !== '';

    if (!isFreeOrBasic && hasValidClassCode) {
      const isValid = await validateClassCode(classCode);
      if (!isValid) {
        clearDeferredSaveCompletion();
        flushSync(() => {
          setIsSaving(false); // 검증 실패 시 저장 상태 해제
        });
        return;
      }
    }

    try {
      console.log('💾 클래스 및 멤버 정보 일괄 저장 시작');
      
      // 1. 클래스 코드 변경사항 처리 (프리/베이직 플랜이 아니고 유효한 코드가 있는 경우만)
      if (!isFreeOrBasic && hasValidClassCode) {
        const oldClassCode = classData.classCode;
        if (oldClassCode !== classCode) {
          console.log('🔄 클래스 코드 변경 감지:', { old: oldClassCode, new: classCode });
          
          // 기존 코드 해제 (있는 경우)
          if (oldClassCode && oldClassCode !== '000000' && oldClassCode !== '????????') {
            await userDataService.unregisterClassCode(oldClassCode);
          }
          
          // 새 코드 등록
          await userDataService.registerClassCode(classCode, userId, classData.id);
        }
      }

      const mergedMembersByGroup = getMergedMembersByGroup();

      // 2. 삭제된 그룹 확인 (먼저 확인)
      const deletedGroupsList = initialMemberGroups.filter(g => !memberGroups.includes(g));
      const deletedMemberSummaries = await getDeletedMembersSummary(
        memberGroups,
        mergedMembersByGroup,
        deletedGroupsList
      );
      
      // 4. 삭제된 그룹이 있으면 그룹 삭제 경고 팝업 표시 (먼저)
      if (deletedGroupsList.length > 0) {
        setDeletedGroups(deletedGroupsList);
        setShowGroupDeleteWarning(true);
        // 개별 멤버 삭제는 그룹 삭제 모달 확인 후 처리하도록 상태 저장
        if (deletedMemberSummaries.length > 0) {
          setDeletedMembers(deletedMemberSummaries);
        }
        return; // 저장 중단
      }
      
      // 5. 삭제된 멤버가 있으면 경고 팝업 표시 (그룹 삭제가 없는 경우만)
      if (deletedMemberSummaries.length > 0) {
        setDeletedMembers(deletedMemberSummaries);
        setShowDeleteWarning(true);
        return; // 저장 중단
      }
      
      // 5. 멤버 그룹 개수 플랜 제한 (스탠다드 2개, 프로/관리자 15개)
      const groupAccessCheck = checkMemberGroupAccess(user, memberGroups.length);
      if (!groupAccessCheck.allowed) {
        setLicenseLimitMessage(groupAccessCheck.message || '');
        setShowLicenseLimitModal(true);
        return; // 저장 중단
      }
      
      // 6. 삭제된 멤버가 없으면 바로 저장 진행
      setMembersByGroup(mergedMembersByGroup);
      await persistClassAndMembers(memberGroups, mergedMembersByGroup);
    } catch (error) {
      console.error('클래스 저장 실패:', error);
      setError('클래스 정보 저장에 실패했습니다.');
      clearDeferredSaveCompletion();
    } finally {
      flushSync(() => {
        setIsSaving(false);
      });
    }
  };

  // 클래스 초기화 함수
  const handleResetClass = async () => {
    if (isResetting) return;
    
    const userEmail = user.email || '';
    const isConfirmed = resetConfirmText.trim() === userEmail;
    
    if (!isConfirmed) {
      setResetError('계정 이메일을 입력해주세요.');
      return;
    }

    setIsResetting(true);
    setResetError('');

    try {
      // 초기화 실행
      await userDataService.resetClass(userId, classData.id);
      
      // 성공 메시지
      alert('클래스가 성공적으로 초기화되었습니다.');
      
      // 모달 닫기
      setShowResetModal(false);
      setResetConfirmText('');
      setIsResetting(false);
      
      // 캐시 초기화 콜백 호출
      if (onClassReset) {
        onClassReset();
      }
      
      // 클래스 정보 새로고침을 위해 onClose 호출
      if (onMembersChange) {
        onMembersChange();
      }
      onClose();
    } catch (error: any) {
      console.error('클래스 초기화 실패:', error);
      setResetError(error?.message || '클래스 초기화에 실패했습니다. 다시 시도해주세요.');
      setIsResetting(false);
    }
  };

  const canReset = resetConfirmText.trim() === (user.email || '');

  if (!isOpen) return null;

  return (
    <>
      {/* 취소 확인 모달 - 가장 위에 렌더링 */}
      {showCancelConfirm && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={showCancelConfirm}
            onClose={() => setShowCancelConfirm(false)}
            onConfirm={() => {
              setShowCancelConfirm(false);
              onClose();
            }}
            onCancel={() => {
              setShowCancelConfirm(false);
              handleSave();
            }}
            title={[
              "변경사항이 있습니다.",
              "저장하지 않고 나가시겠습니까?",
              "변경사항이 모두 사라집니다."
            ]}
            confirmText="나가기"
            cancelText="저장"
            icon="!"
            iconBackground="var(--status-exempted)"
          />
        </div>
      )}

      {/* [-그룹] 클릭 시 1차 그룹 삭제 안내 → 그룹 삭제 확인 모달에서 즉시 서버 반영 */}
      {showRemoveGroupConfirmModal && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={showRemoveGroupConfirmModal}
            onClose={() => setShowRemoveGroupConfirmModal(false)}
            onConfirm={() => {
              setShowRemoveGroupConfirmModal(false);
              setDeletedGroups([selectedMemberGroup]);
              setDeletedMembers([]);
              setShowGroupDeleteWarning(true);
            }}
            onCancel={() => setShowRemoveGroupConfirmModal(false)}
            title={[
              `${getGroupDisplayName(selectedMemberGroup)} 그룹을 삭제하시겠습니까?`,
              '다음 그룹과 그룹에 속한 모든 멤버의 데이터가 완전히 삭제됩니다.',
              '그룹 삭제를 계속하시겠습니까?'
            ]}
            confirmText="계속"
            cancelText="취소"
            icon="!"
            iconBackground="var(--status-pending)"
          />
        </div>
      )}

      {showGroupSwitchConfirm && pendingGroupAction && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={showGroupSwitchConfirm}
            onClose={() => {
              setShowGroupSwitchConfirm(false);
              setPendingGroupAction(null);
            }}
            onCancel={() => {
              const action = pendingGroupAction;
              setShowGroupSwitchConfirm(false);
              setPendingGroupAction(null);

              if (!action) return;

              const { restoredGroups } = discardGroupEditingChanges();
              applyGroupAction(action, restoredGroups);
            }}
            onConfirm={() => {
              const action = pendingGroupAction;
              setShowGroupSwitchConfirm(false);
              setPendingGroupAction(null);

              if (!action) return;

              handleSave({
                afterSaveAction: () => applyGroupAction(action),
                suppressSuccessModal: true
              });
            }}
            title={
              hasOnlyMemberGroupOrderChanges
                ? [
                    '그룹 순서 변경사항이 저장되지 않았습니다.',
                    '그룹 순서 변경을 저장하지 않고 이동하시겠습니까?'
                  ]
                : [
                    '현재 그룹의 변경사항이 저장되지 않았습니다.',
                    '저장하지 않고 이동하시겠습니까?'
                  ]
            }
            cancelText="저장하지 않고 이동"
            confirmText="저장하고 이동"
            icon="!"
            iconBackground="var(--status-pending)"
          />
        </div>
      )}

      {/* 저장 완료 모달 (과제 체크 모달과 동일 스타일) */}
      {showSaveSuccessModal && (
        <div onClick={(e) => e.stopPropagation()}>
          <SaveSuccessOverlay onClick={() => setShowSaveSuccessModal(false)} />
          <SaveSuccessContainer>
            <SaveSuccessContent>
              <SaveSuccessIcon>✓</SaveSuccessIcon>
              <SaveSuccessTitle>저장이 완료되었습니다</SaveSuccessTitle>
            </SaveSuccessContent>
            <SaveSuccessFooter>
              <ContinueEditingButton onClick={() => setShowSaveSuccessModal(false)}>
                편집 계속
              </ContinueEditingButton>
              <CloseModalButton
                onClick={() => {
                  setShowSaveSuccessModal(false);
                  if (onMembersChange) onMembersChange();
                  onClose();
                }}
              >
                편집 종료
              </CloseModalButton>
            </SaveSuccessFooter>
          </SaveSuccessContainer>
        </div>
      )}

      {/* 삭제 확인 팝업 */}
      {showDeleteWarning && (
        <DeleteWarningModal
          deletedMembers={deletedMembers}
          isProcessing={isDeleteProcessing}
          onConfirm={async () => {
            try {
              setIsDeleteProcessing(true);
              const mergedMembersByGroup = getMergedMembersByGroup();
              setMembersByGroup(mergedMembersByGroup);
              await persistClassAndMembers(memberGroups, mergedMembersByGroup);
              setShowDeleteWarning(false);
              setDeletedMembers([]);
            } catch (error) {
              console.error('멤버 데이터 저장 실패:', error);
              setError('멤버 데이터 저장에 실패했습니다.');
              clearDeferredSaveCompletion();
            } finally {
              setIsDeleteProcessing(false);
            }
          }}
          onCancel={() => {
            setShowDeleteWarning(false);
            setDeletedMembers([]);
            setIsDeleteProcessing(false);
            clearDeferredSaveCompletion();
          }}
        />
      )}

      {/* 그룹 삭제 확인 팝업 */}
      {showGroupDeleteWarning && (
        <GroupDeleteWarningModal
          deletedGroups={deletedGroups}
          groupNames={{ ...initialMemberGroupNames, ...memberGroupNames }}
          isProcessing={isDeleteProcessing}
          onConfirm={async () => {
            try {
              setIsDeleteProcessing(true);
              // 그룹 삭제 처리: 각 그룹의 모든 멤버 삭제
              for (const groupKey of deletedGroups) {
                const groupMembers = await userDataService.loadClassMembers(userId, classData.id, groupKey);
                if (groupMembers.length > 0) {
                  const memberIds = groupMembers.map(m => m.id);
                  await userDataService.deleteStudentsWithAssignmentCleanup(userId, classData.id, memberIds, groupKey);
                }
                // 그룹 전용 컬렉션/문서 정리
                await userDataService.deleteMemberGroupData(userId, classData.id, groupKey);
              }

              const mergedBefore = getMergedMembersByGroup();
              const remainingGroups = memberGroups.filter(g => !deletedGroups.includes(g));
              const remainingByGroup = { ...mergedBefore };
              deletedGroups.forEach(g => delete remainingByGroup[g]);
              const cleanedNames = { ...memberGroupNames };
              deletedGroups.forEach(g => delete cleanedNames[g]);
              const cleanedOrder = sanitizeMemberGroupOrder(
                remainingGroups,
                memberGroupOrder.filter(g => !deletedGroups.includes(g))
              );

              setShowGroupDeleteWarning(false);
              setDeletedGroups([]);
              setMemberGroups(remainingGroups);
              setMemberGroupNames(cleanedNames);
              setMemberGroupOrder(cleanedOrder);
              setMembersByGroup(remainingByGroup);

              const nextGroup = cleanedOrder[0] ?? '';
              if (nextGroup) {
                setSelectedMemberGroup(nextGroup);
                setMembers(remainingByGroup[nextGroup] ?? []);
              }

              // 그룹 삭제 처리 후, 개별 멤버 삭제가 있으면 멤버 삭제 모달 표시
              if (deletedMembers.length > 0) {
                setShowDeleteWarning(true);
              } else {
                await persistClassAndMembers(
                  remainingGroups,
                  remainingByGroup,
                  nextGroup || selectedMemberGroup,
                  cleanedNames,
                  cleanedOrder
                );
              }
            } catch (error) {
              console.error('그룹 삭제 저장 실패:', error);
              setError('그룹 삭제 저장에 실패했습니다.');
              clearDeferredSaveCompletion();
            } finally {
              setIsDeleteProcessing(false);
            }
          }}
          onCancel={() => {
            setShowGroupDeleteWarning(false);
            setDeletedGroups([]);
            setDeletedMembers([]);
            setIsDeleteProcessing(false);
            clearDeferredSaveCompletion();
          }}
        />
      )}
      
      <ModalOverlay onClick={handleCancel}>
      <ModalWrapper>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeaderRow>
            <ModalTitle>클래스 설정</ModalTitle>
            <ResetClassButtonHeader onClick={() => setShowResetModal(true)}>
              클래스 초기화
            </ResetClassButtonHeader>
          </ModalHeaderRow>

          <CategorySection>
            <SectionTitle>클래스 코드</SectionTitle>
            <ClassCodeDisplay 
              onDoubleClick={handleClassCodeDoubleClick}
              style={{
                opacity: (user.userType === 'free' || user.userType === 'basic') ? 0.5 : 1,
                cursor: (user.userType === 'free' || user.userType === 'basic') ? 'not-allowed' : 'pointer'
              }}
            >
              <div style={{ 
                flex: 1, 
                textAlign: 'center',
                minWidth: 0,
                overflow: 'hidden'
              }}>
                {isEditingCode ? (
                  <ClassCodeInput
                    type="text"
                    value={classCode}
                    onChange={(e) => handleClassCodeChange(e.target.value)}
                    onBlur={handleClassCodeBlur}
                    maxLength={8}
                    autoFocus
                    disabled={user.userType === 'free' || user.userType === 'basic'}
                  />
                ) : (
                  <span style={{ 
                    transition: 'all 0.3s ease',
                    display: 'inline-block',
                    opacity: (!classCode || classCode.trim() === '') ? 0.5 : 1
                  }}>
                    {classCode && classCode.trim() !== '' ? classCode : '????????'}
                  </span>
                )}
              </div>
              <RandomCodeButton 
                onClick={handleRandomCodeGenerate}
                disabled={user.userType === 'free' || user.userType === 'basic'}
                style={{
                  opacity: (user.userType === 'free' || user.userType === 'basic') ? 0.5 : 1,
                  cursor: (user.userType === 'free' || user.userType === 'basic') ? 'not-allowed' : 'pointer'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 8l-4 4h3c0 3.31-2.69 6-6 6-1.01 0-1.97-.25-2.8-.7l-1.46 1.46C8.97 19.54 10.43 20 12 20c4.42 0 8-3.58 8-8h3l-4-4zM6 12c0-3.31 2.69-6 6-6 1.01 0 1.97.25 2.8.7l1.46-1.46C15.03 4.46 13.57 4 12 4c-4.42 0-8 3.58-8 8H1l4 4 4-4H6z"/>
                </svg>
              </RandomCodeButton>
            </ClassCodeDisplay>
            {(user.userType === 'standard' || user.userType === 'pro' || user.userType === 'admin') && (
              <>
                <EntryCodeTitle>클래스 입장코드 설정</EntryCodeTitle>
                <EntryCodeRow>
                  <EntryCodeInput
                    type="text"
                    inputMode="numeric"
                    value={classEntryCode}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setClassEntryCode(numericValue);
                    }}
                    placeholder="4자리 숫자"
                    maxLength={4}
                  />
                </EntryCodeRow>
              </>
            )}
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </CategorySection>

          <CategorySection>
            <SectionTitle>클래스 멤버</SectionTitle>
            {(() => {
              const maxMemberGroups = getLicenseLimits(user.userType).maxMemberGroups ?? 1;
              const canAddGroup = orderedMemberGroups.length < maxMemberGroups;
              const canRemoveGroup = orderedMemberGroups.length > 1;
              const showGroupButtons = maxMemberGroups > 1;
              return (
                <>
                  {showGroupButtons && (
                    <MemberGroupActionsRow>
                      <AddGroupButton
                        type="button"
                        disabled={!canAddGroup}
                        onClick={() => {
                          if (!canAddGroup) return;
                          requestGroupAction({ type: 'add' });
                        }}
                        title={canAddGroup ? '멤버 그룹 추가' : `현재 플랜에서는 최대 ${maxMemberGroups}개 그룹까지 사용 가능`}
                      >
                        + 그룹
                      </AddGroupButton>
                      <RemoveGroupButton
                        type="button"
                        onClick={handleRemoveGroupClick}
                        disabled={!canRemoveGroup}
                        title="현재 선택된 그룹 삭제"
                      >
                        - 그룹
                      </RemoveGroupButton>
                    </MemberGroupActionsRow>
                  )}
                  {orderedMemberGroups.length >= 1 && (
                    <MemberGroupTabsSection>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleGroupDragEnd}
                      >
                        <SortableContext items={orderedMemberGroups} strategy={rectSortingStrategy}>
                          <MemberGroupTabs>
                            {orderedMemberGroups.map((g) => (
                              editingGroupKey === g ? (
                                <GroupNameInput
                                  key={g}
                                  type="text"
                                  value={editingGroupName}
                                  placeholder="이름 입력"
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= 5) {
                                      setEditingGroupName(value);
                                    }
                                  }}
                                  onBlur={handleFinishEditGroupName}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleFinishEditGroupName();
                                    } else if (e.key === 'Escape') {
                                      handleCancelEditGroupName();
                                    }
                                  }}
                                  autoFocus
                                  maxLength={5}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <SortableGroupTab
                                  key={g}
                                  groupKey={g}
                                  isActive={selectedMemberGroup === g}
                                  label={getGroupDisplayName(g)}
                                  onClick={() => {
                                    if (editingGroupKey && editingGroupKey !== g) {
                                      handleFinishEditGroupName();
                                    }
                                    if (selectedMemberGroup !== g) {
                                      requestGroupAction({ type: 'select', groupKey: g });
                                    }
                                  }}
                                  onDoubleClick={(e) => handleStartEditGroupName(g, e)}
                                />
                              )
                            ))}
                          </MemberGroupTabs>
                        </SortableContext>
                      </DndContext>
                    </MemberGroupTabsSection>
                  )}
                </>
              );
            })()}
            {/* 프리/베이직은 1개 그룹, 스탠다드는 2개, 프로/관리자는 최대 15개 그룹 */}
            <MemberTableContainer>
              <MemberHeader $managerColumn={isManagerEditMode}>
                {isManagerEditMode && <HeaderCell>M</HeaderCell>}
                <HeaderCell>번호</HeaderCell>
                <HeaderCell>이름</HeaderCell>
                <HeaderCell>입장코드</HeaderCell>
                <HeaderCell>삭제</HeaderCell>
                <HeaderCell>이동</HeaderCell>
              </MemberHeader>
              
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={members.map(member => member.id)} strategy={verticalListSortingStrategy}>
                  <MemberList>
                    {members.map((member) => (
                      <SortableMemberRow
                        key={member.id}
                        member={member}
                        updateMember={updateMember}
                        removeMember={removeMember}
                        isManagerEditMode={isManagerEditMode}
                        onManagerChange={updateMemberManager}
                      />
                    ))}
                    
                    <AddMemberRow>
                      <AddMemberButton onClick={addMember}>
                        +
                      </AddMemberButton>
                    </AddMemberRow>
                    <AddMemberRow style={{ marginTop: 0, paddingTop: 4 }}>
                      {isManagerEditMode ? (
                        <ManagerPermissionButton
                          type="button"
                          onClick={() => setIsManagerEditMode(false)}
                        >
                          매니저 권한 부여 닫기
                        </ManagerPermissionButton>
                      ) : (
                        <ManagerPermissionButton
                          type="button"
                          onClick={() => setIsManagerEditMode(true)}
                        >
                          매니저 권한 부여
                        </ManagerPermissionButton>
                      )}
                    </AddMemberRow>
                  </MemberList>
                </SortableContext>
              </DndContext>
            </MemberTableContainer>
          </CategorySection>

          <ButtonGroup>
            <CancelButton data-variant="modal-secondary" onClick={handleCancel}>닫기</CancelButton>
            <SaveButton onClick={() => void handleSave()} disabled={isSaving}>
              {isSaving ? '저장 중...' : '저장'}
            </SaveButton>
          </ButtonGroup>
        </ModalContent>
      </ModalWrapper>
    </ModalOverlay>

    {/* 클래스 초기화 확인 모달 */}
    {showResetModal && (
      <ResetModalOverlay onClick={() => !isResetting && setShowResetModal(false)}>
        <ResetModalContent onClick={(e) => e.stopPropagation()}>
          <ResetModalTitle>클래스 초기화</ResetModalTitle>
          <ResetMessage>
            클래스를 초기화하면 클래스 멤버 정보만 유지되며, 해당 클래스 데이터가 모두 초기화됩니다. 이 작업은 되돌릴 수 없습니다.
          </ResetMessage>
          <ResetMessage>
            클래스 초기화를 계속하려면 계정 이메일을 입력하세요.
          </ResetMessage>
          <ResetInput
            value={resetConfirmText}
            onChange={(e) => setResetConfirmText(e.target.value)}
            placeholder="계정 이메일을 입력"
            disabled={isResetting}
          />
          {resetError && <ResetErrorMessage>{resetError}</ResetErrorMessage>}
          <ResetButtonGroup>
            <ResetCancelButton 
              onClick={() => {
                setShowResetModal(false);
                setResetConfirmText('');
                setResetError('');
              }}
              disabled={isResetting}
            >
              취소
            </ResetCancelButton>
            <ResetConfirmButton
              onClick={handleResetClass}
              disabled={!canReset || isResetting}
            >
              {isResetting ? '초기화 중...' : '초기화 진행'}
            </ResetConfirmButton>
          </ResetButtonGroup>
        </ResetModalContent>
      </ResetModalOverlay>
    )}
    
    <LicenseLimitModal
      isOpen={showLicenseLimitModal}
      message={licenseLimitMessage}
      onClose={() => setShowLicenseLimitModal(false)}
      onUpgrade={() => {
        setShowLicenseLimitModal(false);
        onLicenseClick?.();
      }}
      showUpgradeButton={true}
    />
    
    <WarningModal
      isOpen={showWarningModal}
      message={warningMessage}
      onClose={() => setShowWarningModal(false)}
    />
    </>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  z-index: 10000;
  overflow-y: auto;
  padding: 80px 0 150px 0;
`;

const ModalWrapper = styled.div`
  max-width: 600px;
  width: 90%;
  flex-shrink: 0;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--modal-shadow);
  width: 100%;
  min-width: 0;
  margin-bottom: 150px;
  border: var(--modal-border);
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const CategorySection = styled.div`
  margin-top: 20px;
  background: var(--color-section-bg);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--color-section-border);

  &:first-of-type {
    margin-top: 0;
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 1.1rem;
  color: var(--color-settings-section-title);
  font-weight: 600;
  background: var(--color-section-title-bg);
  padding: 4px 16px;
  border: 1px solid var(--color-section-title-border);
  border-radius: 20px;
  display: inline-block;
  margin-bottom: 20px;
  text-align: center;
  width: 100%;
`;

const EntryCodeTitle = styled.h4`
  margin: 16px 0 8px 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
`;

const EntryCodeRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const EntryCodeInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-panel);
  color: var(--color-text-primary);
  font-size: 1rem;
  text-align: center;
  font-weight: 600;
  font-family: monospace;

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.2);
  }
`;

const MemberGroupActionsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  width: 100%;
  margin: 0 0 12px 0;
`;

const MemberGroupTabsSection = styled.div`
  width: 100%;
  margin: 0 0 12px 0;
`;

const MemberGroupTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
`;

const MemberGroupTab = styled.button<{ $isActive: boolean }>`
  border: 1px solid var(--color-border);
  background: ${props => (props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-panel)')};
  color: ${props => (props.$isActive ? '#ffffff' : 'var(--color-text-primary)')};
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: grab;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${props => (props.$isActive ? 'var(--color-accent)' : 'var(--color-border)')};
  }

  &:active {
    cursor: grabbing;
  }

  :root:not([data-theme='light']) & {
    border-color: rgba(255, 255, 255, 0.18);
  }
`;

const AddGroupButton = styled.button`
  border: 1px solid var(--color-border);
  background: var(--color-panel);
  color: var(--color-text-primary);
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  width: 100%;
  display: inline-flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: var(--color-border);
  }

  :root:not([data-theme='light']) & {
    border-color: rgba(255, 255, 255, 0.18);
  }
`;

const RemoveGroupButton = styled.button`
  border: 1px solid var(--color-border);
  background: var(--color-panel);
  color: var(--color-text-primary);
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  width: 100%;
  display: inline-flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: var(--color-border);
  }

  :root:not([data-theme='light']) & {
    border-color: rgba(255, 255, 255, 0.18);
  }
`;

// 저장 완료 모달 스타일 (과제 체크 모달과 동일)
const SaveSuccessOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 30000;
`;

const SaveSuccessContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 16px;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  z-index: 30001;
  width: 90vw;
  max-width: 400px;
  overflow: hidden;
`;

const SaveSuccessContent = styled.div`
  padding: 32px 24px 24px;
  text-align: center;
`;

const SaveSuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--color-accent-strong);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  margin: 0 auto 20px;
`;

const SaveSuccessTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const SaveSuccessFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 24px 24px;
`;

const ContinueEditingButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-accent-text);
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CloseModalButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--color-accent);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const GroupDeleteBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
`;

const GroupNameInput = styled.input`
  border: 2px solid var(--color-accent-strong);
  background: var(--color-panel);
  color: var(--color-text-primary);
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 0.85rem;
  font-weight: 700;
  outline: none;
  min-width: 60px;
  max-width: 100px;
  text-align: center;

  &::placeholder {
    color: rgba(128, 128, 128, 0.6);
    opacity: 1;
  }

  :root:not([data-theme='light']) & {
    border-color: var(--color-accent-strong);
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }
`;

const ClassCodeDisplay = styled.div`
  background: var(--color-panel);
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: bold;
  font-family: monospace;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;

  &:hover {
    background: var(--color-border);
    border-color: var(--color-text-muted);
    transform: scale(1.02);
  }

  &:focus {
    outline: none;
    background: var(--color-border);
  }
`;

const ClassCodeInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  font-size: 1.5rem;
  font-weight: bold;
  font-family: monospace;
  text-align: center;
  flex: 1;
  padding: 0;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RandomCodeButton = styled.button`
  background: transparent;
  color: #6c757d;
  border: none;
  padding: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    color: var(--color-text-secondary);
    transform: scale(1.2) rotate(180deg);
  }

  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  text-align: center;
  margin-top: 8px;
`;

const MemberTableContainer = styled.div`
  border: 1px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  min-width: 0;
`;

const MemberList = styled.div`
  width: 100%;
  min-width: 0;
`;

const MemberHeader = styled.div<{ $managerColumn?: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.$managerColumn ? '36px minmax(40px, 1fr) minmax(80px, 2fr) minmax(70px, 1.5fr) 40px 40px' : 'minmax(40px, 1fr) minmax(80px, 2fr) minmax(70px, 1.5fr) 40px 40px'};
  background: var(--color-panel);
  border-bottom: 1px solid var(--color-border);
  width: 100%;
`;

const HeaderCell = styled.div`
  padding: 6px 2px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-align: center;
  border-right: 1px solid var(--color-border);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }
`;

const MemberRow = styled.div<{ $managerColumn?: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.$managerColumn ? '36px minmax(40px, 1fr) minmax(80px, 2fr) minmax(70px, 1.5fr) 40px 40px' : 'minmax(40px, 1fr) minmax(80px, 2fr) minmax(70px, 1.5fr) 40px 40px'};
  border-bottom: 1px solid var(--color-border);
  width: 100%;
  background: var(--color-surface);
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }
`;

const MemberInput = styled.input`
  padding: 6px 2px;
  border: none;
  border-right: 1px solid var(--color-border);
  outline: none;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  background: var(--color-surface);
  color: var(--color-text-primary);

  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
  }
`;

const MemberCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid var(--color-border);
  transition: background-color 0.2s ease;

  &:last-child {
    border-right: none;
  }
`;

const ManagerCheckbox = styled.input.attrs({ type: 'checkbox' })`
  accent-color: var(--color-accent-strong);
  cursor: pointer;
  width: 18px;
  height: 18px;
`;

const NameCellWithBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
  padding: 0 2px;
`;

const NameInputWrap = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;

  input {
    width: 100%;
    min-width: 0;
  }
`;

const ManagerBadgeSlot = styled.div`
  width: 26px;
  min-width: 26px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ManagerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: var(--color-accent-strong);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
`;

const DeleteMemberButton = styled.button`
  background: transparent;
  color: #6c757d;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    color: var(--color-text-secondary);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  color: #6c757d;
  transition: color 0.2s ease;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    color: var(--color-text-secondary);
  }
`;

const AddMemberRow = styled.div`
  padding: 12px;
  text-align: center;
  background: var(--color-panel);
`;

const ManagerPermissionButton = styled.button`
  background: var(--color-panel);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-surface);
    color: var(--color-text-primary);
  }
`;

const AddMemberButton = styled.button`
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #3a5f5a;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
  margin-bottom: 20px;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'var(--color-accent)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? '#ccc' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: transparent;
  color: var(--color-accent);
  border: 1px solid var(--color-accent);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.1);
    border-color: var(--color-accent);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
`;

// 삭제 경고 팝업 전용 스타일드 컴포넌트들
const DeleteWarningOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20000; // 기존 모달보다 높은 z-index
`;

const DeleteWarningWrapper = styled.div`
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const DeleteWarningContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--modal-shadow-large);
  width: 100%;
  min-width: 0;
  border: 2px solid #dc3545; // 경고를 위한 빨간색 테두리
`;

// 삭제 확인 팝업 컴포넌트
const DeleteWarningModal: React.FC<{
  deletedMembers: DeletedMemberSummary[];
  isProcessing?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ deletedMembers, isProcessing = false, onConfirm, onCancel }) => {
  return (
    <DeleteWarningOverlay onClick={() => !isProcessing && onCancel()}>
      <DeleteWarningWrapper>
        <DeleteWarningContent onClick={(e) => e.stopPropagation()}>
          <ModalTitle>⚠️ 멤버 삭제 확인</ModalTitle>
          
          <Section>
            <SectionTitle>삭제될 멤버 목록</SectionTitle>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#dc3545', marginBottom: '12px' }}>
                다음 멤버들의 <strong>모든 데이터가 완전히 삭제</strong>됩니다:
              </p>
              <p style={{ color: '#856404', fontSize: '0.9rem', marginBottom: '12px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
                ⚠️ <strong>포함되는 데이터:</strong> 멤버 정보, 일반 과제 상태, 프로젝트 과제 상태, 루틴 과제 상태 등 모든 관련 데이터
              </p>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {deletedMembers.map(({ member, groupName }) => (
                  <li key={member.id} style={{ 
                    padding: '8px 12px', 
                    margin: '4px 0', 
                    background: 'var(--color-panel)', 
                    borderRadius: '4px',
                    border: '1px solid var(--color-border)'
                  }}>
                    <GroupDeleteBadge style={{ marginRight: '8px', fontSize: '0.8rem', padding: '4px 8px' }}>
                      {groupName}
                    </GroupDeleteBadge>
                    <strong>{member.number}번</strong> {member.name}
                  </li>
                ))}
              </ul>
            </div>
            
            <p style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: '16px' }}>
              이 작업은 되돌릴 수 없습니다. 계속하시겠습니까?
            </p>
          </Section>

          {isProcessing && (
            <p style={{ margin: '0 0 16px 0', color: 'var(--color-text-secondary)', textAlign: 'center' }}>
              삭제 중입니다. 잠시만 기다려주세요...
            </p>
          )}

          <ButtonGroup>
            <CancelButton data-variant="modal-secondary" onClick={onCancel} disabled={isProcessing}>취소</CancelButton>
            <SaveButton onClick={onConfirm} style={{ background: '#dc3545' }} disabled={isProcessing}>
              {isProcessing ? '삭제 중...' : '삭제 확인'}
            </SaveButton>
          </ButtonGroup>
        </DeleteWarningContent>
      </DeleteWarningWrapper>
    </DeleteWarningOverlay>
  );
};

// 그룹 삭제 확인 팝업 컴포넌트
const GroupDeleteWarningModal: React.FC<{
  deletedGroups: string[];
  groupNames: { [groupKey: string]: string };
  isProcessing?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ deletedGroups, groupNames, isProcessing = false, onConfirm, onCancel }) => {
  const getGroupDisplayName = (groupKey: string): string => {
    return groupNames[groupKey] || `${groupKey}그룹`;
  };

  return (
    <DeleteWarningOverlay onClick={() => !isProcessing && onCancel()}>
      <DeleteWarningWrapper>
        <DeleteWarningContent onClick={(e) => e.stopPropagation()}>
          <ModalTitle>⚠️ 그룹 삭제 확인</ModalTitle>
          
          <Section>
            <SectionTitle>삭제될 그룹 목록</SectionTitle>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#dc3545', marginBottom: '12px' }}>
                다음 그룹과 그룹속 모든 멤버들의 <strong>모든 데이터가 완전히 삭제</strong>됩니다:
              </p>
              <p style={{ color: '#856404', fontSize: '0.9rem', marginBottom: '12px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
                ⚠️ <strong>포함되는 데이터:</strong> 그룹 정보, 멤버 정보, 일반 과제 상태, 프로젝트 과제 상태, 루틴 과제 상태 등 모든 관련 데이터
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginTop: '12px' }}>
                {deletedGroups.map(groupKey => (
                  <GroupDeleteBadge key={groupKey}>
                    {getGroupDisplayName(groupKey)}
                  </GroupDeleteBadge>
                ))}
              </div>
            </div>
            
            <p style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: '16px' }}>
              이 작업은 되돌릴 수 없습니다. 계속하시겠습니까?
            </p>
          </Section>

          {isProcessing && (
            <p style={{ margin: '0 0 16px 0', color: 'var(--color-text-secondary)', textAlign: 'center' }}>
              그룹 삭제 및 정리 중입니다. 잠시만 기다려주세요...
            </p>
          )}

          <ButtonGroup>
            <CancelButton data-variant="modal-secondary" onClick={onCancel} disabled={isProcessing}>취소</CancelButton>
            <SaveButton onClick={onConfirm} style={{ background: '#dc3545' }} disabled={isProcessing}>
              {isProcessing ? '삭제 중...' : '삭제 확인'}
            </SaveButton>
          </ButtonGroup>
        </DeleteWarningContent>
      </DeleteWarningWrapper>
    </DeleteWarningOverlay>
  );
};

// 초기화 모달 오버레이
const ResetModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 20000;
`;

// 초기화 모달 컨텐츠
const ResetModalContent = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 500px;
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  z-index: 20001;
  max-height: 90vh;
  overflow-y: auto;
`;

const ResetModalTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-text-primary);
`;

const ResetMessage = styled.p`
  margin: 0 0 12px 0;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-text-primary);
  text-align: left;
  
  strong {
    font-weight: 600;
  }
`;

const ResetInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  background: var(--color-surface);
  color: var(--color-text-primary);
  margin: 12px 0;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ResetErrorMessage = styled.div`
  color: var(--color-accent-strong);
  font-size: 0.9rem;
  margin: 8px 0;
`;

const ResetButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const ResetCancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ResetConfirmButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

export default ClassSettingsModal;